import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useDroplet } from './DropletContext';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  role: string | null;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Deliberately authApi, not the currently-selected droplet's api — the
  // home droplet is the only one with real username/password + session
  // auth, so login state must never depend on which droplet is selected
  // for viewing data (see DropletContext).
  const { authApi } = useDroplet();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [role, setRole] = useState<string | null>(null);

  const bootstrap = useCallback(async () => {
    setStatus('loading');
    const session = await authApi.fetchSession();
    if (session.authenticated) {
      setRole(session.role ?? null);
      setStatus('authenticated');
    } else {
      setRole(null);
      setStatus('unauthenticated');
    }
  }, [authApi]);

  useEffect(() => {
    // Runs once on mount — auth state is tied to the home droplet's session
    // only, never to which droplet is currently selected for viewing data.
    void bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Any request that comes back 401 (expired/invalid/missing session)
    // drops the app back to the login screen, not just that one request.
    authApi.setUnauthorizedHandler(() => {
      setRole(null);
      setStatus('unauthenticated');
    });
    return () => authApi.setUnauthorizedHandler(null);
  }, [authApi]);

  const login = useCallback(
    async (username: string, password: string) => {
      const result = await authApi.login(username, password);
      if (result.success) {
        setRole(result.role ?? null);
        setStatus('authenticated');
        return { success: true };
      }
      return { success: false, error: result.error || 'Invalid credentials' };
    },
    [authApi]
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    setRole(null);
    setStatus('unauthenticated');
  }, [authApi]);

  return (
    <AuthContext.Provider value={{ status, role, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
